import React, {useMemo} from 'react'
import {useUserPreferences} from '@data/queries/preferences/userPreferences.main'
import {useWebsiteMenuInformation} from '@data/queries/website/websiteMenuInformation.main'
import hasWebpSupport from '@helpers/misc/hasWebpSupport'
import isServerSide from '@helpers/misc/isServerSide'
import useShouldHidePrice from '@hooks/useShouldHidePrice'
import useWebsiteId from '@hooks/useWebsiteId'
import {useSelectedProduct, useSelectedProductId} from '@page-components/OrderV2/useSelectedProduct'

import useCategoriesAndProducts from '../../../data/queries/categories/getCategoriesAndProducts.main'
import NextOpenDate from '../NextOpenDate/NextOpenDate.main'
import useLayout from '../useLayout'
import useProductsLayout from '../useProductsLayout'

import ProductsContent from './ProductsContent'
import useSetOutOfStockProducts from './useSetOutOfStockProducts'

const ProductsContentMemo = React.memo(ProductsContent)

export interface Props {
  skipServerPreload?: boolean
}

function ProductsInner(props: Props) {
  const {website} = useWebsiteMenuInformation()
  const preferencesV2 = useUserPreferences(website._id)
  const shouldHidePrice = useShouldHidePrice()
  const selectedProduct = useSelectedProduct()
  const selectedProductId = useSelectedProductId()
  const {menuId, storeId, deliverAt} = preferencesV2 ?? {}
  const websiteId = useWebsiteId()

  const format = hasWebpSupport() ? 'webp' : 'jpeg'
  const {categories} = useCategoriesAndProducts({
    websiteId,
    menuId,
    format,
    storeId,
    deliverAt,
    omit: isServerSide() && props.skipServerPreload
  })

  const {design} = useWebsiteMenuInformation()
  const productCardDesign = design?.design.productCardDesign
  const layout = useLayout()

  /**
   * TODO: Reverted changed from nicoleguerrero/ep-1039 because the main query is cached,
   * so a change in the stock is not reflected automatically en the query.
   * It is needed to separate the query in the stable values (cached) and the dynamic ones (non cached)
   * before deleting this logic
   * cc Niko
   */
  const {outOfStockIds} = useSetOutOfStockProducts({websiteId, categories, storeId, deliverAt})
  const memoizedCategories = useMemo(() => {
    if (!(categories?.items && outOfStockIds)) return categories
    const newCategories = {items: []}
    newCategories.items = categories.items.map(category => {
      const newProducts = category.products.map(product => {
        return {...product, isOutOfStock: !!outOfStockIds.get(product._id)}
      })

      return {...category, products: newProducts}
    })

    return newCategories
  }, [outOfStockIds, categories])

  const productsLayout = useProductsLayout(website)

  const nextOpenDate = <NextOpenDate deliverAt={deliverAt} storeId={storeId} />

  return (
    <ProductsContentMemo
      layout={layout}
      menuId={menuId}
      selectedProduct={selectedProduct}
      selectedProductId={selectedProductId}
      hidePrice={shouldHidePrice}
      productsLayout={productsLayout}
      storeId={storeId}
      nextOpenDate={nextOpenDate}
      website={website}
      categories={memoizedCategories}
      productCardDesign={productCardDesign}
    />
  )
}

export default function Products(props: Props) {
  return <ProductsInner {...props} />
}
